import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  StyleSheet,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../../constants/Colors';
import Style from '../../../../../constants/Style';
import BackIcon from '../../../../../components/BackIcon';
import EllipsisIcon from '../../../../../components/EllipsisIcon';
import Button from '../../../../../components/Button';
import AvatarIcon from '../../../../../components/AvatarIcon';
import TabSelectionMobile from '../../../../../components/TabSelectionMobile';
import PressableStyled, { PressableState } from '../../../../../components/PressableStyled';
import SelectionModal from '../../../../../components/SelectionModal';
import {
  AssetInstance,
  Template,
  TemplateField,
  TemplateGroup,
  TemplateIssue,
  TemplateIssueCategoryTitles,
  TemplateTypeTitles,
  UserGroup,
} from '../../../../../constants/Types';
import { getFileNameFromPath, openInNewTab, showValueWithHyphenFallback } from '../../../../../constants/Utils';
import getAssets from '../../../../../requests/Assets/GetAssets';

type TemplateDetailsItem = Template & {
  templateGroupName: string,
};

const TEMPLATE_DETAILS_TABS = {
  TEMPLATE_DATA: 'Template Data',
  APPCLIP: 'AppClip',
  PPM: 'PPM',
  ASSETS: 'Assets',
} as const;

type TemplateDetailsTab = (typeof TEMPLATE_DETAILS_TABS)[keyof typeof TEMPLATE_DETAILS_TABS];

interface TemplateDetailsScreenProps {
  template: TemplateDetailsItem,
  templateGroups: TemplateGroup[],
  onBack: () => void,
  userGroups?: UserGroup[],
  canEditTemplate?: boolean,
  canDeleteTemplate?: boolean,
  // eslint-disable-next-line no-unused-vars
  onEditTemplate?: (template: TemplateDetailsItem) => void,
  // eslint-disable-next-line no-unused-vars
  onDeleteTemplate?: (template: TemplateDetailsItem) => void,
}

interface AccordionSectionProps {
  title: string,
  children: React.ReactNode,
  initiallyExpanded?: boolean,
}

const AccordionSection = ({
  title,
  children,
  initiallyExpanded = true,
}: AccordionSectionProps) => {
  const [expanded, setExpanded] = useState<boolean>(initiallyExpanded);

  return <View style={styles.sectionCard}>
    <Pressable
      style={styles.sectionHeader}
      onPress={() => setExpanded(!expanded)}
    >
      <Text style={styles.sectionTitle}>{title}</Text>
      <FontAwesome
        name={expanded ? 'angle-up' : 'angle-down'}
        size={20}
        color={Colors.light.hereworksBlack500}
      />
    </Pressable>
    {expanded && <View style={styles.sectionBody}>{children}</View>}
  </View>;
};

const Row = ({ label, value }: { label: string, value: string }) => <View style={styles.row}>
  <Text style={styles.rowLabel}>{label}</Text>
  <Text style={styles.rowValue}>{showValueWithHyphenFallback(value)}</Text>
</View>;

const toFieldList = (value: unknown): TemplateField[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((field: unknown) => !!field && typeof field === 'object')
    .map((field: Record<string, unknown>) => ({
      Name: `${field.Name ?? field.name ?? ''}`,
      Format: `${field.Format ?? field.format ?? ''}`,
      IsMandatory: !!(field.IsMandatory ?? field.isMandatory),
    }))
    .filter((field: TemplateField) => field.Name.trim().length > 0);
};

const findStringValue = (
  source: Record<string, unknown>,
  keys: string[],
): string | null => {
  const foundKey = keys.find((key: string) => typeof source[key] === 'string'
    && `${source[key]}`.trim().length > 0);
  if (!foundKey) return null;
  return `${source[foundKey]}`;
};

const findAssetString = (asset: Record<string, unknown>, keys: string[]): string => {
  const value = findStringValue(asset, keys);
  return value ?? '-';
};

const formatTemplatePropertyLabel = (key: string): string => {
  if (key === 'DISABLEPHOTOUPLOAD') return 'DISABLE PHOTO UPLOAD';
  return key;
};

export default function TemplateDetailsScreen({
  template,
  templateGroups,
  onBack,
  userGroups = [],
  canEditTemplate = false,
  canDeleteTemplate = false,
  onEditTemplate,
  onDeleteTemplate,
}: TemplateDetailsScreenProps) {
  const templateData = template as unknown as Record<string, unknown>;

  const templateGroupName = templateGroups
    ?.find((group: TemplateGroup) => group.ButtonPrototypeGroupId === template.templateGroupName
      || group.Name === template.templateGroupName
      || group.buttonPrototypes?.some((item) => item.id === template.id))?.Name
    ?? template.templateGroupName;

  const additionalFields: TemplateField[] = toFieldList(
    template.customfields ?? templateData.customFields ?? [],
  );

  const hasAppClipData: boolean = !!(
    template.title?.trim().length
    || template.subtitle?.trim().length
    || template.subtitleUrl?.trim().length
    || template.issues?.length
  );

  const ppmUrl = findStringValue(templateData, ['ppmUrl', 'PpmUrl', 'ppmURL', 'PpmURL'])
    ?? template.ppmUrl;
  const hasPpmData: boolean = !!ppmUrl?.trim().length;

  const [assets, setAssets] = useState<AssetInstance[]>([]);
  const [assetsLoading, setAssetsLoading] = useState<boolean>(true);
  const [assetsError, setAssetsError] = useState<string>('');

  useEffect(() => {
    if (!template?.id) {
      setAssets([]);
      setAssetsLoading(false);
      return;
    }

    setAssetsLoading(true);
    setAssetsError('');
    getAssets({ templateId: template.id }).then((response: any) => {
      setAssets(response?.assets ?? []);
    }).catch((error) => {
      setAssets([]);
      setAssetsError(error?.message ?? 'Could not load assets for this template.');
    }).finally(() => setAssetsLoading(false));
  }, [template?.id]);

  const availableTabs: TemplateDetailsTab[] = useMemo(() => {
    const tabs: TemplateDetailsTab[] = [TEMPLATE_DETAILS_TABS.TEMPLATE_DATA];
    if (hasAppClipData) tabs.push(TEMPLATE_DETAILS_TABS.APPCLIP);
    if (hasPpmData) tabs.push(TEMPLATE_DETAILS_TABS.PPM);
    tabs.push(TEMPLATE_DETAILS_TABS.ASSETS);
    return tabs;
  }, [hasAppClipData, hasPpmData]);

  const [selectedTab, setSelectedTab] = useState<TemplateDetailsTab>(availableTabs[0]);

  const appClipIssues: TemplateIssue[] = (template.issues ?? [])
    .filter((issue: TemplateIssue) => issue?.Enabled ?? true)
    .filter((issue: TemplateIssue) => issue?.Text?.trim().length > 0);

  const getGroupNames = (issue: TemplateIssue): string => {
    const groupIds = issue?.UserGroupId ?? [];
    if (!groupIds?.length) return '-';
    const names = userGroups
      .filter((group: UserGroup) => groupIds.includes(group.UserGroupId))
      .map((group: UserGroup) => group.Name);
    return names.length ? names.join(', ') : groupIds.join(', ');
  };

  const extraTemplateProperties: [string, unknown][] = Object.entries(templateData)
    .filter(([key]) => ![
      'name', 'id', 'type', 'issues', 'customfields', 'templateGroupName',
      'title', 'subtitle', 'subtitleUrl', 'ppmUrl',
    ].includes(key))
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value));

  const ppmFileName: string = ppmUrl ? getFileNameFromPath(ppmUrl) : '-';
  const ppmFileSize: string = findStringValue(templateData, ['PpmFileSize', 'ppmFileSize'])
    ?? showValueWithHyphenFallback(`${templateData.PpmSize ?? templateData.ppmSize ?? ''}`);

  const maintenanceResponsibleName = findStringValue(templateData, [
    'MaintenanceResponsibleName',
    'maintenanceResponsibleName',
    'NextMaintenanceResponsibleName',
    'nextMaintenanceResponsibleName',
  ]) ?? '-';

  const maintenanceRepeatFrequency = findStringValue(templateData, [
    'MaintenanceRepeatFrequency',
    'maintenanceRepeatFrequency',
    'NextMaintenanceRepeatFrequency',
    'nextMaintenanceRepeatFrequency',
  ]) ?? '-';

  const [templateOptionsVisible, setTemplateOptionsVisible] = useState<boolean>(false);
  const templateActionAvailable = canEditTemplate || canDeleteTemplate;

  return <View style={styles.container}>
    <View style={styles.header}>
      <PressableStyled onPress={onBack} style={styles.iconButton}>
        <BackIcon />
      </PressableStyled>
      <Text style={styles.title} numberOfLines={1}>{template.name}</Text>
      <PressableStyled
        style={styles.iconButton}
        onPress={() => setTemplateOptionsVisible(true)}
        disabled={!templateActionAvailable}
      >
        <EllipsisIcon fill={Colors.light.hereworksBlue400} />
      </PressableStyled>
    </View>

    <TabSelectionMobile
      options={availableTabs}
      selectOption={setSelectedTab}
      selectedOption={selectedTab}
    />

    <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
      {selectedTab === TEMPLATE_DETAILS_TABS.TEMPLATE_DATA && <>
        <AccordionSection title='Template Settings'>
          <Row label='Name' value={template.name} />
          <Row label='Template Type' value={TemplateTypeTitles[template.type] ?? '-'} />
          <Row label='Template Group' value={templateGroupName} />
          {extraTemplateProperties.map(([key, value]) => <Row
            key={`template_prop_${key}`}
            label={formatTemplatePropertyLabel(key)}
            value={`${value ?? ''}`}
          />)}
        </AccordionSection>

        <AccordionSection title='Additional Fields'>
          {!additionalFields.length && <Text style={styles.emptyText}>{'No additional fields'}</Text>}
          {additionalFields.map((field: TemplateField, index: number) => <View
            key={`additional_field_${index}`}
            style={styles.fieldCard}
          >
            <Row label='Name' value={field.Name} />
          </View>)}
        </AccordionSection>
      </>}

      {selectedTab === TEMPLATE_DETAILS_TABS.APPCLIP && <>
        <AccordionSection title='AppClip Settings'>
          <Row label='Title' value={template.title ?? ''} />
          <Row label='Subtitle' value={template.subtitle ?? ''} />
          <Row label='AppClip URL' value={template.subtitleUrl ?? ''} />
        </AccordionSection>

        <AccordionSection title='Issues Section'>
          {!appClipIssues.length && <Text style={styles.emptyText}>{'No issues configured'}</Text>}
          {appClipIssues.map((issue: TemplateIssue, index: number) => {
            const issueData = issue as unknown as Record<string, unknown>;
            const status = issueData.Status ?? issueData.status;

            return <View key={`appclip_issue_${index}`} style={styles.issueCard}>
              <Text style={styles.issueTitle}>{issue.Text}</Text>
              <Row label='Responsible group' value={getGroupNames(issue)} />
              <Row
                label='Category'
                value={TemplateIssueCategoryTitles[issue.Category] ?? `${issue.Category ?? '-'}`}
              />
              {status && <Row label='Status' value={`${status}`} />}
            </View>;
          })}
        </AccordionSection>

        {!!template.subtitleUrl?.trim().length && <Button
          title='Live Preview'
          affirmative
          style={styles.livePreviewButton}
          onPress={() => openInNewTab(template.subtitleUrl)}
        />}
      </>}

      {selectedTab === TEMPLATE_DETAILS_TABS.PPM && <>
        <AccordionSection title='PPM Form Section'>
          <Row label='File name' value={ppmFileName} />
          <Row label='File size' value={ppmFileSize} />
          {!!ppmUrl?.trim().length && <Button
            title='View / Open'
            affirmative={false}
            style={styles.openButton}
            onPress={() => openInNewTab(ppmUrl)}
          />}
        </AccordionSection>

        <AccordionSection title='Next Maintenance Section'>
          <View style={[Style.row, styles.maintenanceHeader]}>
            <AvatarIcon />
            <View style={styles.maintenanceInfo}>
              <Text style={styles.maintenanceName}>{maintenanceResponsibleName}</Text>
              <Text style={styles.maintenanceFrequency}>{maintenanceRepeatFrequency}</Text>
            </View>
          </View>
          <Row label='Responsible person' value={maintenanceResponsibleName} />
          <Row label='Repeat frequency' value={maintenanceRepeatFrequency} />
        </AccordionSection>
      </>}

      {selectedTab === TEMPLATE_DETAILS_TABS.ASSETS && <AccordionSection title='Template Assets'>
        {assetsLoading && <View style={styles.assetsLoadingContainer}>
          <ActivityIndicator color={Colors.light.hereworksBlue500} />
          <Text style={styles.assetsLoadingText}>{'Loading assets...'}</Text>
        </View>}

        {!assetsLoading && !!assetsError.length && <Text style={styles.assetsErrorText}>
          {assetsError}
        </Text>}

        {!assetsLoading && !assetsError.length && !assets.length && <Text style={styles.emptyText}>
          {'No assets found for this template'}
        </Text>}

        {!assetsLoading && !assetsError.length && assets.map((asset: AssetInstance) => {
          const assetData = asset as unknown as Record<string, unknown>;

          return <View key={`template_asset_${asset.id}`} style={styles.assetCard}>
            <Text style={styles.assetTitle}>
              {findAssetString(assetData, ['name', 'Name'])}
            </Text>
            <Row label='Asset ID' value={findAssetString(assetData, ['id', 'Id'])} />
            <Row
              label='Building'
              value={findAssetString(assetData, ['locationBuilding', 'LocationBuilding'])}
            />
            <Row label='Floor' value={findAssetString(assetData, ['locationFloor', 'LocationFloor'])} />
            <Row label='Area' value={findAssetString(assetData, ['locationArea', 'LocationArea'])} />
            <Row label='Space' value={findAssetString(assetData, ['locationSpace', 'LocationSpace'])} />
          </View>;
        })}
      </AccordionSection>}
    </ScrollView>

    <SelectionModal
      isVisible={templateOptionsVisible}
      hide={() => setTemplateOptionsVisible(false)}
      title={template.name ?? 'Template options'}
      listContent={<>
        {canEditTemplate && <Pressable
          style={({ hovered, pressed }: PressableState) => [
            styles.option,
            hovered && Style.listItemHovered,
            pressed && Style.listItemPressed,
          ]}
          onPress={() => {
            setTemplateOptionsVisible(false);
            onEditTemplate?.(template);
          }}
        >
          <Text style={styles.optionText}>{'Edit'}</Text>
        </Pressable>}
        {canDeleteTemplate && <Pressable
          style={({ hovered, pressed }: PressableState) => [
            styles.option,
            hovered && Style.listItemHovered,
            pressed && Style.listItemPressed,
          ]}
          onPress={() => {
            setTemplateOptionsVisible(false);
            onDeleteTemplate?.(template);
          }}
        >
          <Text style={styles.optionText}>{'Delete'}</Text>
        </Pressable>}
      </>}
    />
  </View>;
}

const styles = StyleSheet.create({
  assetCard: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    backgroundColor: Colors.light.hereworksBackground,
  },
  assetsErrorText: {
    fontSize: 13,
    lineHeight: 18,
    color: Colors.light.hereworksRed500,
    fontFamily: 'Roboto_400Regular',
  },
  assetsLoadingContainer: {
    ...Style.row,
    justifyContent: 'flex-start',
  },
  assetsLoadingText: {
    marginStart: 8,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
  },
  assetTitle: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    marginBottom: 8,
  },
  container: {
    flex: 1,
    backgroundColor: '#FFF',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    paddingBottom: 32,
  },
  emptyText: {
    fontSize: 13,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  fieldCard: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
  },
  header: {
    ...Style.row,
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 4,
    backgroundColor: '#FFF',
  },
  iconButton: {
    height: 36,
    width: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  issueCard: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    backgroundColor: Colors.light.hereworksBackground,
  },
  issueTitle: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    marginBottom: 8,
  },
  livePreviewButton: {
    alignSelf: 'stretch',
    marginTop: 4,
    flex: undefined,
    height: 40,
  },
  maintenanceFrequency: {
    fontSize: 12,
    lineHeight: 16,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
  },
  maintenanceHeader: {
    justifyContent: 'flex-start',
    marginBottom: 8,
  },
  maintenanceInfo: {
    marginStart: 10,
  },
  maintenanceName: {
    fontSize: 14,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_500Medium',
  },
  openButton: {
    alignSelf: 'flex-start',
    marginTop: 4,
    flex: undefined,
    marginStart: 0,
  },
  option: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    marginHorizontal: -24,
  },
  optionText: {
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Roboto_400Regular',
  },
  row: {
    marginBottom: 8,
  },
  rowLabel: {
    fontSize: 11,
    color: Colors.light.hereworksBlack400,
    textTransform: 'uppercase',
    fontFamily: 'Roboto_500Medium',
    lineHeight: 16,
  },
  rowValue: {
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    lineHeight: 20,
    marginTop: 2,
  },
  sectionBody: {
    marginTop: 8,
  },
  sectionCard: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    backgroundColor: '#FFF',
  },
  sectionHeader: {
    ...Style.row,
  },
  sectionTitle: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
  },
  title: {
    flex: 1,
    fontSize: 20,
    lineHeight: 28,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    marginHorizontal: 8,
  },
});
